// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Constants
import { VIDEO_CONFIG } from '@/constants';

// Utils
import { hexToRgba } from '@/utils/styles';
import { lalezarFontFamily } from '@/styles/fonts';

const { trainingHighlights } = employeeYearInReviewTheme.rawColors;

export const SCENE5B_LIMITS = {
  maxLatestTrainings: 5,
  maxCardsPerGroup: 3,
  maxAvatarsPerCard: 4,
};

export const SCENE5B_COLORS = {
  background: trainingHighlights.background,
  circle: trainingHighlights.circle,
  card: trainingHighlights.card,
  textTitle: trainingHighlights.textPrimary,
  textDate: hexToRgba(trainingHighlights.textPrimary, 82),
  avatarRing: hexToRgba(trainingHighlights.avatarRing, 5),
  avatarFallback: trainingHighlights.avatarFallback,
};

const pitchX = 609.25;
const circle = { left: 257, top: 225, size: 381 };

const card = {
  left: 65.25,
  top: 332.5,
  width: 537,
  height: 340,
  clipPath:
    'polygon(100.0000% 100.0000%, 17.2756% 100.0000%, 0.0000% 27.6471%, 77.9702% 0.0000%)',
};

const avatar = {
  top: 740,
  size: 70,
  border: 4,
  containerLeft: 205,
  gap: 18,
};

// Auto-center: compute bounding box of all visual elements, then offset to screen center
const avatarCenterXPositions = [243, 343, 450, 550];
const avatarHalfExtent = Math.ceil(avatar.size / 2 + avatar.border);
const allBounds = [
  circle.left,
  circle.left + circle.size,
  card.left,
  card.left + card.width,
  Math.min(...avatarCenterXPositions) - avatarHalfExtent,
  Math.max(...avatarCenterXPositions) + avatarHalfExtent,
];
const groupCenterX = (Math.min(...allBounds) + Math.max(...allBounds)) / 2;
const centerOffsetX = VIDEO_CONFIG.width / 2 - groupCenterX;

export const SCENE5B_LAYOUT = {
  group: { pitchX, centerOffsetX },
  circle: {
    top: circle.top,
    left: circle.left,
    width: circle.size,
    height: circle.size,
  },
  card: {
    top: card.top,
    left: card.left,
    width: card.width,
    height: card.height,
    clipPath: card.clipPath,
  },
  title: {
    baseFontSize: 78,
    minFontSize: 42,
    maxLines: 3,
    safeAreaWidth: 350,
    safeAreaHeight: 265,
    paddingLeft: 40,
    lineHeightRatio: 1.125,
    leftInCard: 95,
    topInCard: 75,
  },
  date: { left: 205, top: 680 },
  avatar,
};

export const SCENE5B_TIMING = {
  revealStart: 8,
  revealDuration: 22,
  avatarStartOffset: 4,
  avatarStagger: 5,
  avatarDuration: 16,
};

// Frame when text + date reveal finishes
export const SCENE5B_REVEAL_END_FRAME =
  SCENE5B_TIMING.revealStart + SCENE5B_TIMING.revealDuration;

// Reveal animation frame range [start, end]
export const SCENE5B_REVEAL_FRAME_RANGE = [
  SCENE5B_TIMING.revealStart,
  SCENE5B_REVEAL_END_FRAME,
] as const;

// First avatar animation start frame
export const SCENE5B_AVATAR_START_FRAME =
  SCENE5B_REVEAL_END_FRAME + SCENE5B_TIMING.avatarStartOffset;

export const SCENE5B_DURATION = {
  minGroupDuration: 140,
  minSceneDuration: 180,
  baseReadFrames: 68,
  perCardReadFrames: 8,
};

export const SCENE5B_ANIMATION = {
  background: {
    riseDistance: 40,
    spring: { damping: 20, mass: 0.8, stiffness: 100 },
  },
  date: {
    opacityStops: [0, 0.45, 1] as const,
    opacityValues: [0, 0.72, 1] as const,
  },
  avatar: {
    opacity: [0, 0.86, 1] as const,
    opacityStops: [0, 0.6, 1] as const,
    scale: [0.86, 1] as const,
    translateY: [16, 0] as const,
    translateX: [-10, 0] as const,
    spring: { damping: 12, mass: 0.7, stiffness: 120 },
  },
};

export const SCENE5B_CURVED_LINE_PATH =
  'M-673.661 154.942c316.156-120.855 431.225 128.583 463.486 342.061 20.358 134.716 83.549 390.026 342.039 492.286 270.486 107.011 543.756-187.078 787.4 90.141 243.646 277.21 482.616 212.46 480.656 94.12-1.96-118.33-331.11-108.27-590.979-321.991S742.086 72.125-.479-10.866c-122.034-7.807-373.237-65.386-443.871-287.015-88.777-278.552-138.365-403.644-506.806-547.432';

// Slide animation duration between card groups (frames)
export const SCENE5B_SLIDE_DURATION = 24;

// Extra frames after cards finish for line retraction to complete
export const SCENE5B_LINE_RETRACT_BUFFER = SCENE5B_SLIDE_DURATION * 3;

// Background exit animation starts this many frames before the content exit
export const SCENE5B_BG_EXIT_LEAD_FRAMES = 120;

export const SCENE5B_FONTS = {
  title: {
    fontFamily: lalezarFontFamily,
    fontWeight: '400',
    letterSpacing: '0.02em',
  },
  date: {
    fontFamily: 'var(--font-michroma)',
    fontWeight: '300',
    fontSize: 30,
    lineHeight: '50px',
    letterSpacing: '0.02em',
  },
};
